import { FC, useEffect, useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'

import { Spinner as SpinnerComponent } from '@forto-dev/phoenix-ui-core/common'
import { Splash } from '@forto-dev/phoenix-ui-core/ship'

import { getConfig } from '@/app/config'
import { useAuth } from '@/features/auth'
import { useUserProfile } from '@/features/auth/api/useUserProfile'
import { routeTree } from '@/routes'
import { useFeatureFlag } from '@/utils/featureFlag/useFeatureFlag'
import { userlane } from '@/utils/userlane/UserlaneService'

import { AppSkeleton } from './appSkeleton'
import { createErrorFallback } from './create-error-fallback'
import { DefaultNotFoundComponent } from './defaultNotFoundComponent.tsx'

const router = createRouter({
  routeTree,
  basepath: getConfig().APP_BASE_URL ? `${getConfig().APP_BASE_URL}/` : '/',
  // TODO: Better error message
  defaultErrorComponent: createErrorFallback('An error occurred while loading this section'),
  defaultPendingComponent: () => (
    <Splash>
      <SpinnerComponent size="lg" />
    </Splash>
  ),
  defaultNotFoundComponent: DefaultNotFoundComponent,
  // Based on https://tanstack.com/router/latest/docs/framework/react/guide/data-loading#passing-all-loader-events-to-an-external-cache
  defaultPreloadStaleTime: 0,
  // This means that the data will be fetched using the route's `loader` when a user hovers or `touchstart`s a link
  defaultPreload: 'intent',
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

export const Router: FC = () => {
  const { customer, isReady, isLoggingIn, isAuthenticated, user, loggedInAsCustomer, isCustomerActive } = useAuth()
  const queryClient = useQueryClient()
  const { isUserProfileLoading } = useUserProfile()
  const { isFeatureFlagsLoading, isFeatureEnabled } = useFeatureFlag()
  const routerContext = useMemo(() => {
    return {
      isReady,
      isCustomerActive,
      isFeatureEnabled,
      customer,
    }
  }, [isReady, isCustomerActive, customer, isFeatureEnabled])

  useEffect(() => {
    if (isAuthenticated && user && customer) {
      userlane.init(user, customer, loggedInAsCustomer)
    }
  }, [isAuthenticated, user, customer, loggedInAsCustomer])

  useEffect(() => {
    router.invalidate()
  }, [routerContext])

  if (isLoggingIn || !isReady || isUserProfileLoading || isFeatureFlagsLoading) {
    return <AppSkeleton />
  }

  return (
    <RouterProvider
      context={{
        isReady,
        isCustomerActive,
        isFeatureEnabled,
        customer,
        queryClient,
      }}
      router={router}
    />
  )
}
